/**
 * Позиция медиа-фрагмента для превью: Safari при preload="metadata" залипает на
 * HAVE_METADATA и не декодирует ни одного кадра, пока элемент не сикнули, — превью
 * остаётся чёрным (Chrome нулевой кадр рисует сам). Фрагмент `#t` даёт media-стеку
 * стартовую позицию, и кадр декодируется уже на загрузке. 1 мс попадает в нулевой
 * кадр при любом fps, так что картинка ровно та же.
 */
const FIRST_FRAME_FRAGMENT = "#t=0.001"

/**
 * URL видео для `<video preload="metadata">` без `poster`: с фрагментом, иначе в
 * Safari вместо кадра будет чернота. Фрагмент не уходит на сервер — подпись
 * signed_id и ключ браузерного кэша не меняются; на blob:-URL тоже работает.
 * URL с готовым `#`-фрагментом не трогаем: своя позиция важнее нулевого кадра.
 */
export function videoFirstFrameSrc(url: string | null | undefined): string | undefined {
  if (!url) return undefined

  return url.includes("#") ? url : `${url}${FIRST_FRAME_FRAGMENT}`
}

/** Seek HTMLVideoElement and resolve after `seeked` (or immediately if already there). */
export function seekVideoPromise(video: HTMLVideoElement, timeSec: number): Promise<void> {
  return new Promise((resolve) => {
    if (!Number.isFinite(timeSec) || timeSec < 0) {
      resolve()
      return
    }
    const dur = video.duration
    const clamped = Number.isFinite(dur) && dur > 0 ? Math.min(Math.max(0, timeSec), dur - 1e-3) : Math.max(0, timeSec)
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked)
      resolve()
    }
    video.addEventListener("seeked", onSeeked)
    if (Math.abs(video.currentTime - clamped) < 1e-4) {
      video.removeEventListener("seeked", onSeeked)
      queueMicrotask(resolve)
      return
    }
    video.currentTime = clamped
  })
}

type SeekState = { busy: boolean; pending: number | null }

const seekStates = new WeakMap<HTMLVideoElement, SeekState>()

function seekState(video: HTMLVideoElement): SeekState {
  let state = seekStates.get(video)
  if (!state) {
    state = { busy: false, pending: null }
    seekStates.set(video, state)
  }
  return state
}

/**
 * Сик конкретного <video> с коалесингом: одновременно выполняется ровно один,
 * промежуточные цели отбрасываются (побеждает последняя).
 *
 * Возвращает true только у «владельца» — вызова, который довёл элемент до
 * последней запрошенной позиции; остальные получают false и ничего не рисуют.
 * Позицию для подписи брать не из захваченного аргумента, а из актуального
 * источника: владелец мог доехать дальше своей исходной цели.
 *
 * Без этого перетаскивание плейхеда ставило по сику на каждый pointermove:
 * currentTime переписывался поверх незавершённого сика, а сетевые Range-запросы
 * уже были в полёте.
 */
export async function requestVideoFrameAt(video: HTMLVideoElement, timeSec: number): Promise<boolean> {
  const state = seekState(video)
  state.pending = timeSec
  if (state.busy) return false

  state.busy = true
  try {
    while (state.pending != null) {
      const target = state.pending
      state.pending = null
      await seekVideoPromise(video, target)
    }
  } finally {
    state.busy = false
  }
  return true
}

export function drawVideoToCanvas(video: HTMLVideoElement, canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return
  const cw = canvas.width
  const ch = canvas.height
  const scale = Math.min(cw / vw, ch / vh)
  const dw = vw * scale
  const dh = vh * scale
  const ox = (cw - dw) / 2
  const oy = (ch - dh) / 2
  ctx.fillStyle = "#09090b"
  ctx.fillRect(0, 0, cw, ch)
  ctx.drawImage(video, ox, oy, dw, dh)
}
